"use client";

import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BarChart3, ChartBar, Table2 } from "lucide-react";

import { ListSurface, ListSurfaceBody, ListSurfaceHeader } from "@/components/ds";
import { InlineTimeRangeControls } from "@/components/globals";
import { EnterprisePageShell } from "@/components/layout/enterprise-page-shell";
import { HeaderDeviceSelect } from "@/components/layout/header-device-select";
import { StatusIndicator } from "@/components/ui/status-indicator";
import { Card, DataCard } from "@/components/ui-system";
import { getDeviceById, getDevices } from "@/lib/api/endpoints/devices";
import { getTelemetry } from "@/lib/api/endpoints/telemetry";
import type { DeviceChannelDto, TelemetryDto } from "@/lib/api/types";
import {
  deviceActivityModelFromApi,
  formatDeviceOperationalStatusLabel,
  operationalStatusToCardTone,
  operationalStatusToIndicatorVariant,
} from "@/lib/devices/device-state-model";
import { readDeviceDisableState } from "@/lib/devices/device-disable-state";
import { getDeviceSiteLabel } from "@/lib/devices/device-field-helpers";
import { cn } from "@/lib/utils/cn";
import { useAuthStore } from "@/modules/auth/store";
import { entitlementBlockedMessage } from "@/modules/telemetry/entitlement-ui";
import { coerceBooleanTelemetryValue, formatBooleanState, formatTelemetryValue, getChannelDisplayLabel } from "@/modules/telemetry/page-utils";
import {
  type ChannelThresholds,
  getChannelThresholds,
  telemetrySeverityTextClass,
  telemetryThresholdSeverity,
} from "@/modules/telemetry/thresholds";
import { useReportingDeviceStore } from "@/store/reporting-device";
import { useTimeRangeStore } from "@/store/time-range";
import { useWorkspaceChromeStore } from "@/store/workspace-chrome";

type AnalyticsViewMode = "graph" | "table";
type AggregatorKey = "avg" | "sum" | "min" | "max" | "last" | "count";

const AGGREGATOR_ORDER: AggregatorKey[] = ["avg", "sum", "min", "max", "last", "count"];
const DEFAULT_ANALYTICS_AGGREGATORS: AggregatorKey[] = ["avg"];

const AGGREGATOR_LABELS: Record<AggregatorKey, string> = {
  avg: "Average",
  sum: "Sum",
  min: "Minimum",
  max: "Maximum / highest",
  last: "Last value",
  count: "Count",
};

const AGGREGATOR_COLORS: Record<AggregatorKey, string> = {
  avg: "bg-info",
  sum: "bg-accent",
  min: "bg-success",
  max: "bg-primary",
  last: "bg-warning",
  count: "bg-error",
};

type ChannelAnalyticsRow = {
  channel: DeviceChannelDto;
  values: Partial<Record<AggregatorKey, number | null>>;
};

type DiscreteAnalyticsRow = {
  channel: DeviceChannelDto;
  latest: unknown;
  sampleCount: number;
  trueCount: number | null;
  falseCount: number | null;
  uniqueValues: string[];
};

export function AnalyticsPage() {
  const tenantId = useAuthStore((s) => s.tenantId);
  const user = useAuthStore((s) => s.user);
  const setWorkspaceChrome = useWorkspaceChromeStore((s) => s.setWorkspaceChrome);
  const start = useTimeRangeStore((s) => s.start);
  const end = useTimeRangeStore((s) => s.end);
  const deviceId = useReportingDeviceStore((s) => s.selectedDeviceId);
  const setDeviceId = useReportingDeviceStore((s) => s.setSelectedDeviceId);
  const [viewMode, setViewMode] = useState<AnalyticsViewMode>("graph");
  const allWorkspaces = user?.role === "super_admin" || user?.accountType === "platform";

  const devicesQuery = useQuery({
    queryKey: ["analytics", "devices", allWorkspaces ? "all" : tenantId],
    queryFn: () => getDevices(allWorkspaces ? null : tenantId ?? null, allWorkspaces ? "all" : null, "analytics"),
    enabled: allWorkspaces || Boolean(tenantId),
    staleTime: 30_000,
  });

  const devices = useMemo(() => devicesQuery.data ?? [], [devicesQuery.data]);

  useLayoutEffect(() => {
    setWorkspaceChrome({
      headerAction: (
        <HeaderDeviceSelect
          devices={devices}
          value={deviceId}
          onChange={setDeviceId}
          loading={devicesQuery.isLoading}
          disabled={!allWorkspaces && !tenantId}
        />
      ),
    });
    return () => setWorkspaceChrome({ headerAction: null });
  }, [allWorkspaces, deviceId, devices, devicesQuery.isLoading, setWorkspaceChrome, tenantId]);

  useEffect(() => {
    if (devicesQuery.isLoading) {
      return;
    }
    if (devices.length === 0) {
      if (deviceId) {
        setDeviceId("");
      }
      return;
    }
    if (!deviceId && devices[0]?.id) {
      setDeviceId(devices[0].id);
      return;
    }
    const exists = devices.some((device) => device.id === deviceId);
    if (deviceId && !exists) {
      setDeviceId(devices[0]?.id ?? "");
    }
  }, [deviceId, devices, devicesQuery.isLoading, setDeviceId]);

  const deviceQuery = useQuery({
    queryKey: ["analytics", "device", deviceId, allWorkspaces ? "all" : "tenant"],
    queryFn: () => getDeviceById(deviceId, allWorkspaces ? "all" : null, "analytics"),
    enabled: Boolean(deviceId),
    staleTime: 20_000,
  });
  const disableState = useMemo(() => readDeviceDisableState(deviceQuery.data), [deviceQuery.data]);
  const disableCutoff = disableState?.disabledAt ?? null;
  const disableCutoffKey = disableCutoff?.toISOString() ?? "none";

  const rowsQuery = useQuery({
    queryKey: ["analytics", "rows", deviceId, start.toISOString(), end.toISOString(), allWorkspaces ? "all" : "tenant", disableCutoffKey],
    queryFn: () => {
      const effectiveEnd = disableCutoff && disableCutoff < end ? disableCutoff : end;
      return getTelemetry(deviceId, { start, end: effectiveEnd }, allWorkspaces ? "all" : null, null, "analytics");
    },
    enabled: Boolean(deviceId),
    staleTime: 20_000,
  });

  const numericChannels = useMemo(
    () => (deviceQuery.data?.channels ?? []).filter((channel) => channel.data_type === "number"),
    [deviceQuery.data?.channels],
  );
  const discreteChannels = useMemo(
    () => (deviceQuery.data?.channels ?? []).filter((channel) => channel.data_type !== "number"),
    [deviceQuery.data?.channels],
  );
  const channelThresholds = useMemo(
    () =>
      Object.fromEntries(
        numericChannels.map((channel) => [channel.key, getChannelThresholds(deviceQuery.data, channel)]),
      ),
    [deviceQuery.data, numericChannels],
  );

  const enabledAggregators = useMemo(() => {
    const set = new Set<AggregatorKey>();
    for (const channel of numericChannels) {
      const configured = normalizeAggregators(channel.aggregation);
      for (const key of configured.length > 0 ? configured : DEFAULT_ANALYTICS_AGGREGATORS) {
        set.add(key);
      }
    }
    const ordered = AGGREGATOR_ORDER.filter((key) => set.has(key));
    return ordered.length > 0 ? ordered : (["avg"] as AggregatorKey[]);
  }, [numericChannels]);

  const analyticsRows = useMemo<ChannelAnalyticsRow[]>(() => {
    const rows = rowsQuery.data ?? [];
    return numericChannels.map((channel) => {
      const configured = normalizeAggregators(channel.aggregation);
      const aggregators = configured.length > 0 ? configured : (["avg"] as AggregatorKey[]);
      const values = computeChannelAggregators(rows, channel.key, aggregators);
      return { channel, values };
    });
  }, [numericChannels, rowsQuery.data]);
  const discreteRows = useMemo<DiscreteAnalyticsRow[]>(() => {
    const rows = rowsQuery.data ?? [];
    return discreteChannels.map((channel) => {
      const values = rows
        .map((row) => row.data?.[channel.key])
        .filter((value) => value !== null && value !== undefined);
      const latest = values.length > 0 ? values[values.length - 1] : null;
      if (channel.data_type === "boolean") {
        const normalized = values.map((value) => coerceBooleanTelemetryValue(value)).filter((value): value is boolean => value != null);
        const trueCount = normalized.filter(Boolean).length;
        const falseCount = normalized.length - trueCount;
        return {
          channel,
          latest,
          sampleCount: values.length,
          trueCount,
          falseCount,
          uniqueValues: [],
        };
      }
      const uniqueValues = [...new Set(values.map((value) => formatTelemetryValue(value)))].slice(0, 6);
      return {
        channel,
        latest,
        sampleCount: values.length,
        trueCount: null,
        falseCount: null,
        uniqueValues,
      };
    });
  }, [discreteChannels, rowsQuery.data]);

  const graphMax = useMemo(() => {
    const allValues = analyticsRows.flatMap((row) =>
      enabledAggregators
        .map((key) => row.values[key])
        .filter((value): value is number => typeof value === "number" && Number.isFinite(value)),
    );
    const maxValue = Math.max(...allValues, 0);
    return maxValue > 0 ? maxValue : 1;
  }, [analyticsRows, enabledAggregators]);

  const deviceHealthModel = deviceQuery.data ? deviceActivityModelFromApi(deviceQuery.data) : null;
  const selectedDeviceLabel = deviceQuery.data?.name || deviceQuery.data?.device_uid || "No logger selected";
  const selectedDeviceSite = deviceQuery.data ? getDeviceSiteLabel(deviceQuery.data) : "Choose a logger to start";
  const deviceStatusVariant = deviceHealthModel ? operationalStatusToIndicatorVariant(deviceHealthModel.status) : "neutral";
  const blockedMessage = entitlementBlockedMessage(rowsQuery.error);

  const headerMeta = (
    <>
      <span>{selectedDeviceLabel}</span>
      <span className="text-muted">|</span>
      <span>{numericChannels.length} numeric channels</span>
      <span className="text-muted">|</span>
      <span>{discreteChannels.length} state / text channels</span>
      <span className="text-muted">|</span>
      <span>{enabledAggregators.map((key) => AGGREGATOR_LABELS[key]).join(", ")}</span>
    </>
  );

  const analyticsControls = (
    <div className="flex flex-wrap items-end justify-end gap-ds-3">
      <InlineTimeRangeControls />
      <div className="flex flex-col gap-1">
        <span className="text-micro font-semibold uppercase tracking-[0.12em] text-muted">View</span>
        <div className="segment-tabs">
          {(["graph", "table"] as AnalyticsViewMode[]).map((mode) => (
            <button
              key={mode}
              type="button"
              onClick={() => setViewMode(mode)}
              aria-label={mode === "graph" ? "Graphical view" : "Table view"}
              title={mode === "graph" ? "Graphical view" : "Table view"}
              className={cn(
                "segment-tab inline-flex h-9 w-9 !min-h-9 !px-0",
                viewMode === mode && "segment-tab-active",
              )}
            >
              {mode === "graph" ? (
                <ChartBar className="h-4 w-4" aria-hidden />
              ) : (
                <Table2 className="h-4 w-4" aria-hidden />
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const viewHeaderClassName = "flex flex-col gap-ds-3 border-b border-card-border px-ds-5 py-ds-5 md:flex-row md:items-start md:justify-between";

  return (
    <EnterprisePageShell
      hideHeader
      kicker="Insights"
      kickerIcon={BarChart3}
      title="Analytics"
      description="Channel aggregators for one logger at a time, based on the configured aggregation choices from the device setup."
      meta={headerMeta}
      extra={
        <div className="grid gap-ds-3 md:grid-cols-[minmax(0,1.35fr)_minmax(15rem,0.9fr)]">
          <div
            className={cn(
              "rounded-card border px-ds-4 py-ds-3 shadow-raised",
              operationalStatusToCardTone(deviceHealthModel?.status),
            )}
          >
            <div className="flex flex-wrap items-center justify-between gap-ds-2">
              <p className="text-micro font-semibold uppercase tracking-[0.12em] text-muted">Active logger</p>
              {deviceHealthModel ? (
                <span
                  className={cn(
                    "inline-flex items-center gap-2 rounded-full border px-ds-2.5 py-1 text-micro font-bold uppercase tracking-[0.12em]",
                    deviceHealthModel.status === "ONLINE" && "border-success/35 bg-success/12 text-success",
                    deviceHealthModel.status === "OFFLINE" && "border-card-border bg-surface-secondary text-muted",
                    deviceHealthModel.status === "STALE" && "border-warning/35 bg-warning/12 text-warning",
                    deviceHealthModel.status === "ERROR" && "border-error/35 bg-error/12 text-error",
                    deviceHealthModel.status === "MAINTENANCE" && "border-accent/35 bg-accent/12 text-accent",
                    deviceHealthModel.status === "VISUALIZATION_DISABLED" && "border-warning/35 bg-warning/12 text-warning",
                    deviceHealthModel.status === "INGESTION_DISABLED" && "border-error/35 bg-error/12 text-error",
                  )}
                  title={deviceHealthModel.delayLabel}
                  >
                    <StatusIndicator
                      variant={deviceStatusVariant}
                      size="sm"
                      pulse={deviceHealthModel.status === "ONLINE"}
                      showRing={deviceHealthModel.status === "STALE"}
                    />
                  {formatDeviceOperationalStatusLabel(deviceHealthModel.status)}
                </span>
              ) : null}
            </div>
            <p className="mt-ds-1 text-title font-semibold text-foreground">{selectedDeviceLabel}</p>
            <div className="mt-1 flex flex-wrap items-center gap-x-ds-2 gap-y-1 text-caption text-muted">
              <span>{selectedDeviceSite}</span>
              {deviceHealthModel ? <span className="text-muted/65">|</span> : null}
              {deviceHealthModel ? <span>{deviceHealthModel.delayLabel}</span> : null}
            </div>
          </div>
          <div className="rounded-card border border-card-border bg-surface-secondary px-ds-4 py-ds-3 shadow-raised">
            <p className="text-micro font-semibold uppercase tracking-[0.12em] text-muted">Analytics mode</p>
            <p className="mt-ds-1 text-title font-semibold text-foreground">
              {viewMode === "graph" ? "Graphical summary" : "Table summary"}
            </p>
            <p className="mt-1 text-caption text-muted">
              Aggregator values are computed across the selected time window for the channels configured on this device.
            </p>
          </div>
        </div>
      }
    >
      <div className="space-y-ds-5">
        <div className="grid gap-ds-4 md:grid-cols-2 xl:grid-cols-4">
          <Card
            className={cn(
              "flex flex-col gap-ds-2",
              operationalStatusToCardTone(deviceHealthModel?.status),
            )}
          >
            <div className="flex items-start justify-between gap-ds-2">
              <p className="dl-label">Device</p>
              {deviceHealthModel ? (
                <span
                  className={cn(
                    "inline-flex items-center gap-2 rounded-full border px-ds-2.5 py-1 text-micro font-bold uppercase tracking-[0.12em]",
                    deviceHealthModel.status === "ONLINE" && "border-success/35 bg-success/12 text-success",
                    deviceHealthModel.status === "OFFLINE" && "border-card-border bg-surface-secondary text-muted",
                    deviceHealthModel.status === "STALE" && "border-warning/35 bg-warning/12 text-warning",
                    deviceHealthModel.status === "ERROR" && "border-error/35 bg-error/12 text-error",
                    deviceHealthModel.status === "MAINTENANCE" && "border-accent/35 bg-accent/12 text-accent",
                    deviceHealthModel.status === "VISUALIZATION_DISABLED" && "border-warning/35 bg-warning/12 text-warning",
                    deviceHealthModel.status === "INGESTION_DISABLED" && "border-error/35 bg-error/12 text-error",
                  )}
                  >
                    <StatusIndicator
                      variant={deviceStatusVariant}
                      size="sm"
                      pulse={deviceHealthModel.status === "ONLINE"}
                      showRing={deviceHealthModel.status === "STALE"}
                    />
                  {formatDeviceOperationalStatusLabel(deviceHealthModel.status)}
                </span>
              ) : null}
            </div>
            <p
              className="font-sans text-[2rem] font-bold leading-none tracking-tight text-foreground"
              style={{ letterSpacing: "-0.03em" }}
            >
              {deviceQuery.data?.name || "--"}
            </p>
            <p className="text-caption text-muted">
              {deviceQuery.data ? getDeviceSiteLabel(deviceQuery.data) : "Choose a logger"}
            </p>
            {deviceHealthModel ? (
              <p className="text-micro font-medium text-muted">{deviceHealthModel.delayLabel}</p>
            ) : null}
          </Card>
          <DataCard
            title="Rows in range"
            value={String(rowsQuery.data?.length ?? 0)}
            hint="Telemetry events considered for the aggregations"
          />
          <DataCard
            title="Channels"
            value={String(numericChannels.length)}
            hint={
              deviceQuery.data
                ? `${deviceQuery.data.channels?.length ?? 0} total configured channels`
                : "No numeric channels available"
            }
          />
          <DataCard
            title="Aggregators"
            value={String(enabledAggregators.length)}
            hint={enabledAggregators.map((key) => AGGREGATOR_LABELS[key]).join(", ")}
          />
        </div>

        <Card className="space-y-ds-4 p-ds-5">
          <div className="space-y-1">
            <p className="text-title font-semibold text-foreground">Aggregators</p>
            <p className="text-caption text-muted">
              These are pulled from the channel setup on the selected device and evaluated over the active time range.
            </p>
          </div>
          <div className="flex flex-wrap gap-ds-2">
            {enabledAggregators.map((key) => (
              <span key={key} className="rounded-control border border-accent/30 bg-accent/10 px-ds-3 py-1 text-caption text-accent">
                {AGGREGATOR_LABELS[key]}
              </span>
            ))}
          </div>
        </Card>

        {discreteRows.length > 0 ? (
          <Card className="space-y-ds-4 p-ds-5">
            <div className="space-y-1">
              <p className="text-title font-semibold text-foreground">State and text channels</p>
              <p className="text-caption text-muted">Boolean and string channels use latest-value and distribution summaries instead of numeric aggregators.</p>
            </div>
            <div className="grid gap-ds-3 lg:grid-cols-2">
              {discreteRows.map((row) => (
                <div key={row.channel.id} className="rounded-card border border-card-border bg-surface-secondary px-ds-4 py-ds-4 shadow-raised">
                  <div className="flex flex-wrap items-start justify-between gap-ds-3">
                    <div>
                      <p className="text-title font-semibold text-foreground">{getChannelDisplayLabel(row.channel)}</p>
                      <p className="text-caption text-muted">{row.channel.data_type === "boolean" ? "Boolean state summary" : "String value summary"}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-title font-semibold text-foreground">
                        {row.channel.data_type === "boolean" ? formatBooleanState(row.latest) : formatTelemetryValue(row.latest)}
                      </p>
                      <p className="text-caption text-muted">Latest</p>
                    </div>
                  </div>
                  <div className="mt-ds-4 grid gap-ds-3 sm:grid-cols-3">
                    <div className="rounded-control border border-card-border bg-surface-primary px-ds-3 py-ds-3">
                      <p className="text-micro font-semibold uppercase tracking-[0.12em] text-muted">Samples</p>
                      <p className="mt-1 text-title font-semibold text-foreground">{row.sampleCount}</p>
                    </div>
                    {row.channel.data_type === "boolean" ? (
                      <>
                        <div className="rounded-control border border-card-border bg-surface-primary px-ds-3 py-ds-3">
                          <p className="text-micro font-semibold uppercase tracking-[0.12em] text-muted">True / Open</p>
                          <p className="mt-1 text-title font-semibold text-foreground">{row.trueCount ?? 0}</p>
                        </div>
                        <div className="rounded-control border border-card-border bg-surface-primary px-ds-3 py-ds-3">
                          <p className="text-micro font-semibold uppercase tracking-[0.12em] text-muted">False / Closed</p>
                          <p className="mt-1 text-title font-semibold text-foreground">{row.falseCount ?? 0}</p>
                        </div>
                      </>
                    ) : (
                      <div className="rounded-control border border-card-border bg-surface-primary px-ds-3 py-ds-3 sm:col-span-2">
                        <p className="text-micro font-semibold uppercase tracking-[0.12em] text-muted">Observed values</p>
                        <p className="mt-1 text-caption text-foreground">{row.uniqueValues.length > 0 ? row.uniqueValues.join(", ") : "No values yet"}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        ) : null}

        {disableState ? (
          <Card className={disableState.mode === "ingestion" ? "border-error/30 bg-error/10 text-error" : "border-warning/30 bg-warning/10 text-warning"} role="status">
            {disableState.mode === "ingestion"
              ? "This device has ingestion disabled. Analytics continues to show historical data already stored, but no new telemetry should arrive."
              : "This device has visualization disabled. Analytics is capped at the disable timestamp, and historical data before that point remains visible."}
          </Card>
        ) : null}

        {blockedMessage ? (
          <Card className="border-warning/30 bg-warning/10 text-warning" role="status">
            {blockedMessage}
          </Card>
        ) : null}

        {viewMode === "graph" ? (
          <Card className="overflow-hidden border-card-border bg-surface-primary p-0">
            <div className={viewHeaderClassName}>
              <div className="space-y-1">
                <p className="text-title font-semibold text-foreground">Graphical view</p>
                <p className="text-caption text-muted">Each channel shows the configured aggregator values as comparable horizontal bars.</p>
              </div>
              {analyticsControls}
            </div>

            <div className="p-ds-5">
              {analyticsRows.length === 0 ? (
                <div className="rounded-card border border-dashed border-card-border bg-surface-secondary px-ds-4 py-ds-10 text-center text-caption text-muted" role="status">
                  No numeric channels are available for analytics on this device.
                </div>
              ) : (
                <div className="space-y-ds-4">
                  {analyticsRows.map((row) => (
                    <div key={row.channel.id} className="rounded-card border border-card-border bg-surface-secondary px-ds-4 py-ds-4 shadow-raised">
                      <div className="mb-ds-3 flex flex-wrap items-end justify-between gap-ds-3">
                        <div>
                          <p className="text-title font-semibold text-foreground">{getChannelDisplayLabel(row.channel)}</p>
                          <p className="text-caption text-muted">
                            {row.channel.unit || "Configured channel"}
                          </p>
                        </div>
                      </div>

                      <div className="space-y-ds-3">
                        {enabledAggregators
                          .filter((key) => key in row.values)
                          .map((key) => {
                            const value = row.values[key];
                            const numericValue = typeof value === "number" ? value : 0;
                            const widthPercent = Math.max(4, Math.min(100, (numericValue / graphMax) * 100));
                            return (
                              <div key={key} className="grid gap-ds-3 md:grid-cols-[11rem_minmax(0,1fr)_8rem] md:items-center">
                                <div className="text-caption font-medium text-foreground">{AGGREGATOR_LABELS[key]}</div>
                                <div className="h-3 overflow-hidden rounded-full bg-surface-inset">
                                  <div
                                    className={cn("h-full rounded-full", AGGREGATOR_COLORS[key])}
                                    style={{ width: `${value == null ? 0 : widthPercent}%` }}
                                  />
                                </div>
                                <AnalyticsValueText
                                  value={value}
                                  unit={row.channel.unit}
                                  aggregator={key}
                                  thresholds={channelThresholds[row.channel.key]}
                                  align="right"
                                />
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        ) : (
          <ListSurface>
            <ListSurfaceHeader className="border-card-border px-ds-5 py-ds-5">
              <div className="space-y-1">
                <p className="text-title font-semibold text-foreground">Table view</p>
                <p className="text-caption text-muted">One row per channel, one column per configured aggregator.</p>
              </div>
              {analyticsControls}
            </ListSurfaceHeader>

            <ListSurfaceBody className="overflow-x-auto">
              <table className="min-w-full text-left">
                <thead className="bg-surface-secondary">
                  <tr className="border-b border-card-border">
                    <th className="px-ds-4 py-ds-3 text-[12px] font-semibold uppercase tracking-[0.08em] text-muted">Channel</th>
                    {enabledAggregators.map((key) => (
                      <th key={key} className="whitespace-nowrap px-ds-4 py-ds-3 text-[12px] font-semibold uppercase tracking-[0.08em] text-muted">
                        {AGGREGATOR_LABELS[key]}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {analyticsRows.map((row) => (
                    <tr key={row.channel.id} className="border-b border-card-border odd:bg-accent-muted/40 even:bg-surface-primary">
                      <td className="px-ds-4 py-ds-3">
                        <div>
                          <p className="text-caption font-medium text-foreground">{getChannelDisplayLabel(row.channel)}</p>
                          <p className="text-micro text-muted">
                            {row.channel.unit || "Configured channel"}
                          </p>
                        </div>
                      </td>
                      {enabledAggregators.map((key) => (
                        <td key={key} className="whitespace-nowrap px-ds-4 py-ds-3">
                          <AnalyticsValueText
                            value={row.values[key]}
                            unit={row.channel.unit}
                            aggregator={key}
                            thresholds={channelThresholds[row.channel.key]}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </ListSurfaceBody>
          </ListSurface>
        )}
      </div>
    </EnterprisePageShell>
  );
}

function normalizeAggregators(values: string[] | null | undefined): AggregatorKey[] {
  const normalized = (values ?? []).filter((value): value is AggregatorKey =>
    value === "avg" || value === "sum" || value === "min" || value === "max" || value === "last" || value === "count",
  );
  const seen = new Set<AggregatorKey>();
  return normalized.filter((value) => {
    if (seen.has(value)) return false;
    seen.add(value);
    return true;
  });
}

function computeChannelAggregators(
  rows: TelemetryDto[],
  channelKey: string,
  aggregators: AggregatorKey[],
): Partial<Record<AggregatorKey, number | null>> {
  const numericValues = rows
    .map((row) => row.data?.[channelKey])
    .filter((value): value is number => typeof value === "number" && Number.isFinite(value));

  const count = numericValues.length;
  const sum = count > 0 ? numericValues.reduce((acc, value) => acc + value, 0) : null;
  const avg = count > 0 && sum != null ? sum / count : null;
  const min = count > 0 ? Math.min(...numericValues) : null;
  const max = count > 0 ? Math.max(...numericValues) : null;
  const last = count > 0 ? (numericValues[count - 1] ?? null) : null;

  const source: Record<AggregatorKey, number | null> = {
    avg,
    sum,
    min,
    max,
    last,
    count,
  };

  return Object.fromEntries(aggregators.map((key) => [key, source[key]])) as Partial<Record<AggregatorKey, number | null>>;
}

function formatValueWithUnit(value: number, unit: string | null | undefined, aggregator: AggregatorKey): string {
  if (aggregator === "count") {
    return formatTelemetryValue(value);
  }
  return `${formatTelemetryValue(value)}${unit ? ` ${unit}` : ""}`;
}

function AnalyticsValueText({
  value,
  unit,
  aggregator,
  thresholds,
  align,
}: {
  value: number | null | undefined;
  unit: string | null | undefined;
  aggregator: AggregatorKey;
  thresholds: ChannelThresholds | undefined;
  align?: "right";
}) {
  const severity = telemetryThresholdSeverity(value, thresholds);
  return (
    <span
      className={cn(
        "block text-caption font-semibold",
        align === "right" && "text-right",
        severity ? telemetrySeverityTextClass(severity) : "text-muted",
      )}
    >
      {value == null ? "--" : formatValueWithUnit(value, unit, aggregator)}
    </span>
  );
}
